import { Component, OnInit, ViewChild, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { Dialog } from 'primeng/dialog';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from '../../services/getglobalsettings.service'
import { CurrencyRate, CurrencyRateListItem, EditCurrencyResult } from '../../shared/currency'
import { RestApiService } from '../../services/rest-api.service';

/** Longest currency code the API accepts (`MAX_CURCODE_LENGTH` in the API's `CurrencyRates.ts`). */
const MAX_CURRENCY_CODE_LENGTH = 32;

/**
 * Modal dialog for creating a currency, or adding/editing a rate of an existing one.
 *
 * **Mode comes from `currencyRate.CURID`**, re-evaluated every time the dialog is shown
 * (`onShowDialog`) -- the callers mutate one object in place, so `ngOnChanges` would not fire:
 * - **New currency** (`CURID` null / 0 / negative): code and name are typed in and the rate is
 *   the currency's first one. The caller posts it with `CURID` omitted and the API creates the
 *   `Currencies` row and the rate together.
 * - **Existing currency** (`CURID > 0`): code and name are loaded and editable (`PUT /Currency/:id`) and
 *   the rate value and date can be entered; saving renames the currency and/or adds a new dated rate row,
 *   whichever the user actually changed (`EditCurrencyResult.renamed` / `saveRate`). The item's rate/date are the
 *   *persisted* baseline; the form starts at that value dated today. A rate is only saved when the user touched
 *   the rate or date field AND the (value, day) pair differs from the persisted one -- so a rename alone never
 *   posts a rate, re-entering today's unchanged value on a new day works, and re-entering the persisted pair (an
 *   exact duplicate) is no change. Nothing changed at all is a validation error. Once a rename has been emitted it
 *   is re-sent on every later confirm of the same dialog session (the PUT is idempotent): if the rename went
 *   through but the rate after it failed, the form is then still brought in line with what is stored.
 *
 * `currencyRate` may be either a `CurrencyRate` (`CURRRATE*` fields, from the currency selector)
 * or a `CurrencyRateListItem` (`CRVALUE`/`CRDATE`, from the rates list) -- both are read.
 *
 * On confirm the form is validated and `currRateEntry` emits an `EditCurrencyResult` (code/name are the trimmed
 * form values; `renamed` and `saveRate` say which parts the caller has to save). With
 * `closeOnConfirm` (default) the dialog then closes; a caller that saves asynchronously sets it
 * to `false`, closes the dialog itself on success, reports a failure through `saveError`, and holds `saving`
 * true meanwhile so the dialog stays put until the response is in.
 * `displayChange` emits `false` on hide for two-way `[(display)]` binding.
 */
@Component({
  selector: 'app-edit-currency-dlg',
  templateUrl: './edit-currency-dlg.component.html',
  styleUrls: ['./edit-currency-dlg.component.css']
})
export class EditCurrencyDlgComponent implements OnInit, OnDestroy {

  /** Controls dialog visibility; supports two-way binding via `displayChange`. */
  @Input() display: boolean = false;

  /** Text shown in the dialog title bar. */
  @Input() header: string;

  /** Initial state of the "select after closing" checkbox; echoed back as `EditCurrencyResult.selectAfter`. */
  @Input() selectAfter: boolean = true;

  /** The record to edit; read each time the dialog is shown. */
  @Input() currencyRate: CurrencyRate | CurrencyRateListItem;

  /** When false the dialog stays open after confirm -- the caller closes it once its save succeeded. */
  @Input() closeOnConfirm: boolean = true;

  /** Server-side failure text from the caller's save (e.g. "code already exists"); shown under the form. */
  @Input() saveError: string = '';

  /**
   * True while the caller's save request is in flight. The dialog then cannot be dismissed (X / Escape / Cancel)
   * or edited, so a late response can never land on a dialog the user has meanwhile closed and reopened.
   */
  @Input() saving: boolean = false;

  _currencyId: number = null;
  _currencyCode: string = '';
  _currencyName: string = '';
  _rateValue: number = null;
  _dateValue: Date = null;
  _selectAfter: boolean = true;
  _validationError: string = '';

  // Baselines from the last show, to tell what the user changed (existing currency only).
  private _origCode: string = '';
  private _origName: string = '';
  private _initRate: number = null;
  private _initDay: string = '';
  /** The user edited the rate or date field since the dialog was shown (`markRateTouched`). */
  private _rateTouched: boolean = false;
  /** A rename was already emitted in this dialog session: it is re-sent on every later confirm (see class doc). */
  private _renameEmitted: boolean = false;
  /** False between opening for an existing currency and its code/name arriving (then OK is refused). */
  private _loaded: boolean = true;

  /** True while no real `CURID` is set: code and name are editable and the rate is the first one. */
  get isNewCurrency(): boolean { return !(this._currencyId > 0); }

  /** Emits `false` on dialog hide to support two-way `[(display)]` binding. */
  @Output() displayChange = new EventEmitter();

  /** Emits the validated `EditCurrencyResult` when the user confirms. */
  @Output() currRateEntry = new EventEmitter<EditCurrencyResult>();

  modal: boolean = true;

  settings: GlobalSettingsData;

  private _currencyQuery: Subscription;

  @ViewChild('editcurrency') editCurrencyDialog: Dialog;

  constructor(
    private restapi: RestApiService,
    private globalsettingsservice: GetglobalsettingsService) {
  }

  /** Unsubscribes the in-flight currency lookup. */
  ngOnDestroy() {
    if (this._currencyQuery) { this._currencyQuery.unsubscribe(); }
  }

  /** Loads global settings. The form itself is (re)initialised on every show, see `onShowDialog`. */
  ngOnInit(): void {
    this.settings = this.globalsettingsservice.getGlobalSettings();
  }

  /** Resets the form from the current `currencyRate` input and, for an existing currency, loads its code and name. */
  onShowDialog() {
    if (this._currencyQuery) { this._currencyQuery.unsubscribe(); }

    const rate = this.currencyRate as Partial<CurrencyRate & CurrencyRateListItem> | null;

    this._validationError = '';
    this._selectAfter = this.selectAfter;
    this._currencyId = (rate?.CURID > 0) ? rate.CURID : null;
    this._currencyCode = '';
    this._currencyName = '';
    this._origCode = '';
    this._origName = '';
    this._loaded = !this._currencyId;
    this._rateTouched = false;
    this._renameEmitted = false;

    // Values reach us from API-sourced models, so dates may be strings (see CLAUDE.md "Date fields").
    const rawDate = rate?.CURRRATEDATE ?? rate?.CRDATE;
    const itemDate = rawDate ? new Date(rawDate) : null;
    // The item's rate/date are the persisted baseline. 0 means "not entered yet": leave the field empty.
    this._initRate = (rate?.CURRRATEVALUE ?? rate?.CRVALUE) || null;
    this._initDay = itemDate ? itemDate.toDateString() : '';
    // An existing currency's new rate is dated today by default, whatever the persisted rate's own date is.
    this._dateValue = (this._currencyId || !itemDate) ? new Date() : itemDate;
    this._rateValue = this._initRate;

    if (this._currencyId) {
      const id = this._currencyId;
      this._currencyQuery = this.restapi.getCurrency().subscribe(list => {
        const c = list.find(x => x.CURID == id);
        if (c) {
          this._currencyCode = this._origCode = (c.CURCODE || '').trim();
          this._currencyName = this._origName = (c.CURNAME || '').trim();
          this._loaded = true;
        }
      });
    }
  }

  /** Called when the user edits the rate or the date field: only then can the rate be saved (see class doc). */
  markRateTouched() {
    this._rateTouched = true;
  }

  /** Emits `false` via `displayChange` to close the dialog. */
  onHideDialog() {
    this.displayChange.emit(false);
  }

  /** Validates the form; on success emits the `EditCurrencyResult` and (unless `closeOnConfirm` is false) closes the dialog. */
  onConfirm(text: string) {
    if (this.saving) { return; }
    const plan = this._plan();
    this._validationError = plan.error;
    if (plan.error) { return; }

    const result: EditCurrencyResult = {
      CRID: 0,
      CURID: this._currencyId,
      CURCODE: this._currencyCode.trim(),
      CURNAME: this._currencyName.trim(),
      CRDATE: this._dateValue,
      CRVALUE: this._rateValue,
      selectAfter: this._selectAfter,
      renamed: plan.renamed,
      saveRate: plan.saveRate
    };
    if (plan.renamed) { this._renameEmitted = true; }

    this.currRateEntry.emit(result);
    if (this.closeOnConfirm) { this.display = false; }
  }

  /**
   * Validates the form and works out what the caller has to save. `error` is the first problem as user-facing text
   * ('' when valid); `renamed` / `saveRate` are only meaningful when it is valid.
   */
  private _plan(): { error: string; renamed: boolean; saveRate: boolean } {
    const fail = (error: string) => ({ error, renamed: false, saveRate: false });
    const isNew = this.isNewCurrency;

    if (!isNew && !this._loaded) { return fail('Дані валюти ще завантажуються'); }

    const code = (this._currencyCode || '').trim();
    const name = (this._currencyName || '').trim();
    if (!code) { return fail('Вкажіть код валюти'); }
    if (/\s/.test(code)) { return fail('Код валюти не може містити пробіли'); }
    if (code.length > MAX_CURRENCY_CODE_LENGTH) { return fail('Код валюти не може бути довшим за ' + MAX_CURRENCY_CODE_LENGTH + ' символи'); }
    if (!name) { return fail('Вкажіть назву валюти'); }

    const dateOk = !!this._dateValue && !isNaN(new Date(this._dateValue).getTime());

    if (isNew) {
      if (!(this._rateValue > 0)) { return fail('Вкажіть курс більше нуля'); }
      if (!dateOk) { return fail('Вкажіть дату курсу'); }
      return { error: '', renamed: false, saveRate: true };
    }

    // Existing currency: the rate is optional, and a rate left as pre-filled (same value, same day) is no change.
    if (this._rateValue != null && !(this._rateValue > 0)) { return fail('Курс має бути більше нуля'); }
    const renamed = this._renameEmitted || (code !== this._origCode) || (name !== this._origName);
    const saveRate = this._rateTouched && this._rateValue > 0 && dateOk &&
      ((this._rateValue !== this._initRate) || (new Date(this._dateValue).toDateString() !== this._initDay));
    if (this._rateValue > 0 && !dateOk) { return fail('Вкажіть дату курсу'); }
    if (!renamed && !saveRate) { return fail('Немає змін для збереження'); }
    return { error: '', renamed, saveRate };
  }

}
